/**
 * Welle B (Punkt 15) — Inline-Dokument-Vorschau für den MD-Editor.
 *
 * Wiederverwendung des Board-Vorschau-Prinzips (ScenePreviewOverlay, D-39/D-43):
 * der Ziel-Inhalt wird über den NORMALEN authentifizierten Content-Endpoint
 * geladen, also greift der zentrale Guard (#7) automatisch — es gibt KEINEN
 * eigenen Vorschau-Endpoint und KEINEN eingebrannten Snapshot. Board → SVG-
 * Thumbnail, Notiz → gerendertes Markdown, Liste → Tabellen-Ausschnitt; ein
 * unbekannter kind → Klick-Kachel. 403 → sichtbarer „kein Zugriff"-Platzhalter.
 *
 * Anders als das Szenen-Overlay ist dies eine eigenständige, positionslose
 * Komponente: sie wird per Post-Mount-Pass (RyfeMarkdown) in die
 * `.ryfe-mdpreview`-Platzhalter der Vorschau-Ansicht montiert. Der Anzeige-
 * Modus (original|fit|fill) steuert das Füll-/Crop-Verhalten wie bei Bildern.
 */

import { exportToSvg } from "@excalidraw/excalidraw";
import { useEffect, useRef, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { parseScene } from "../board-data";
import { resolveMainFrame } from "../frame-model";
import { renderMarkdown } from "../markdown";
import { routeForKind } from "../open-file";
import { navigate } from "../router";

import "./ryfe.scss";

export type EmbedMode = "original" | "fit" | "fill";

const KIND_LABEL: Record<string, string> = {
  board: "Board",
  md: "Notiz",
  list: "Liste",
};

type PreviewState =
  | { status: "loading" }
  | { status: "ok"; svg: string }
  | { status: "md"; html: string }
  | { status: "list"; cols: string[]; rows: string[][] }
  | { status: "tile"; kind: string }
  | { status: "denied" }
  | { status: "empty" }
  | { status: "error" };

// SVG-Füllverhalten je Modus (wie object-fit bei Bildern):
//   original → natürliche Größe, oben-links (Overflow wird geclippt)
//   fit      → contain (ganzer Inhalt sichtbar)
//   fill     → cover (füllt die Box, beschneidet Überstand)
const PRESERVE: Record<EmbedMode, string | null> = {
  original: null,
  fit: "xMidYMid meet",
  fill: "xMidYMid slice",
};

export const RyfeDocEmbed = ({
  wsId,
  fileId,
  mode = "fit",
  label,
}: {
  wsId: string;
  fileId: string;
  mode?: EmbedMode;
  label?: string;
}) => {
  const [state, setState] = useState<PreviewState>({ status: "loading" });
  const [kind, setKind] = useState<string>("board");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const meta = await ryfeApi.getFile(wsId, fileId).catch(() => null);
        const k = meta?.kind || "board";
        if (mounted.current) {
          setKind(k);
        }
        if (k === "md") {
          const buf = await ryfeApi.getFileContent(wsId, fileId);
          const text = new TextDecoder()
            .decode(new Uint8Array(buf))
            .slice(0, 1200);
          if (mounted.current) {
            setState({ status: "md", html: renderMarkdown(text) });
          }
          return;
        }
        if (k === "list") {
          const snap = await ryfeApi.getList(wsId, fileId);
          const cols = snap.columns.slice(0, 4);
          const rows = snap.rows.slice(0, 6).map((r) =>
            cols.map((col) => {
              const v = r.data[col.id];
              return v === undefined || v === null ? "" : String(v);
            }),
          );
          if (mounted.current) {
            setState({
              status: "list",
              cols: cols.map((col) => col.name),
              rows,
            });
          }
          return;
        }
        if (k !== "board") {
          if (mounted.current) {
            setState({ status: "tile", kind: k });
          }
          return;
        }
        const buf = await ryfeApi.getFileContent(wsId, fileId);
        const text = new TextDecoder().decode(new Uint8Array(buf));
        const scene = parseScene(text);
        const elements = (scene.elements || []) as never[];
        if (!elements.length) {
          if (mounted.current) {
            setState({ status: "empty" });
          }
          return;
        }
        const mainFrame = resolveMainFrame(elements, meta?.main_frame_id);
        const svg = await exportToSvg({
          elements,
          appState: { ...scene.appState, exportBackground: true },
          files: (scene.files || {}) as never,
          exportingFrame: mainFrame as never,
          skipInliningFonts: true,
        });
        const pa = PRESERVE[mode];
        if (pa) {
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          svg.setAttribute("preserveAspectRatio", pa);
        }
        const str = new XMLSerializer().serializeToString(svg);
        if (mounted.current) {
          setState({ status: "ok", svg: str });
        }
      } catch (e) {
        const denied = e instanceof ApiError && e.status === 403;
        if (mounted.current) {
          setState({ status: denied ? "denied" : "error" });
        }
      }
    })();
  }, [wsId, fileId, mode]);

  const open = () => {
    const route = routeForKind(kind, fileId);
    if (route) {
      navigate(route);
    }
  };

  return (
    <span
      className={`ryfe-docembed ryfe-docembed--${mode}`}
      role="button"
      tabIndex={0}
      title={`Öffnen: ${label || "Dokument"}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      <span className="ryfe-docembed__body">
        {state.status === "loading" ? (
          <span className="ryfe-docembed__note">Vorschau lädt…</span>
        ) : state.status === "ok" ? (
          // eslint-disable-next-line react/no-danger
          <span
            className="ryfe-docembed__svg"
            dangerouslySetInnerHTML={{ __html: state.svg }}
          />
        ) : state.status === "md" ? (
          // eslint-disable-next-line react/no-danger
          <span
            className="ryfe-docembed__md"
            dangerouslySetInnerHTML={{ __html: state.html }}
          />
        ) : state.status === "list" ? (
          <span className="ryfe-docembed__list">
            <table>
              <thead>
                <tr>
                  {state.cols.map((c, i) => (
                    <th key={i}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </span>
        ) : state.status === "tile" ? (
          <span className="ryfe-docembed__note">
            {KIND_LABEL[state.kind] || state.kind} · {label || "öffnen"} ↗
          </span>
        ) : state.status === "denied" ? (
          <span className="ryfe-docembed__note ryfe-docembed__note--denied">
            🔒 kein Zugriff
          </span>
        ) : state.status === "empty" ? (
          <span className="ryfe-docembed__note">leere Szene</span>
        ) : (
          <span className="ryfe-docembed__note">Vorschau nicht verfügbar</span>
        )}
      </span>
      <span className="ryfe-docembed__bar">📄 {label || "Dokument"} ↗</span>
    </span>
  );
};
